// cspell:disable
import { getCurrencyFormatter } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import LocalPickupLogo from "@redotech/redo-icons/building-01.svg";
import TiktokIcon from "@redotech/redo-icons/tiktok-color-icon.svg";
import TruckIcon from "@redotech/redo-icons/truck-02.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  KnownSCAC,
  knownScacToLabel,
  supportedFreightMarketplaces,
} from "@redotech/redo-model/freight/freight";
import {
  carrierToLabel,
  carrierToTrademarkText,
} from "@redotech/redo-model/fulfillments/fulfillment-carriers-and-services";
import {
  RedoShopifyDeliveryMethodType,
  RedoShopifyDeliveryMethodTypeToString,
} from "@redotech/redo-model/fulfillments/fulfillment-delivery-method-type";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { isDoneStatus } from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import { RateProvider } from "@redotech/redo-model/outbound-labels/outbound-labels";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { getCarrierServiceLabel } from "@redotech/redo-web/carrier-service-labels";
import { Flex } from "@redotech/redo-web/flex";
import { getFreightLogoUrl } from "@redotech/redo-web/freight/logo";
import {
  LogoSize,
  ShippingLogo,
} from "@redotech/redo-web/fulfillment/common/shipping-logo";
import { SkeletonText } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { OverflowTooltip } from "@redotech/redo-web/tooltip/overflow-tooltip";
import { isDef } from "@redotech/util/checks";

import { memo, useContext, useMemo, useState } from "react";
import { getShippingSelectionCellData } from "../fulfillment-groups/cells/shipping-selection-cell";
import { AutomationInfoIcon } from "./automation-info-icon";
import { CarrierAccountsContext } from "./carrier-accounts-context";
import { LearningIcon } from "./learning-icon";
import {
  PinVerificationType,
  usePinVerification,
} from "./pin-verification-context";
import * as styles from "./shipment-rate-selection-dropdown.module.css";
import { OutboundRateOption } from "./util";

type LcgInfo =
  | { showLandedCost: false }
  | {
      showLandedCost: true;
      totalLandedCost: number;
      duties: number;
      taxes: number;
      fees: number;
    };

const getLcgInfo = (option: OutboundRateOption | string | null): LcgInfo => {
  if (
    option &&
    typeof option === "object" &&
    "landedCost" in option &&
    option.landedCost
  ) {
    const totalInternationalFees =
      option.landedCost.duties +
      option.landedCost.taxes +
      option.landedCost.fees;
    return {
      showLandedCost: true,
      totalLandedCost: totalInternationalFees,
      duties: option.landedCost.duties,
      taxes: option.landedCost.taxes,
      fees: option.landedCost.fees,
    };
  }
  return { showLandedCost: false };
};

const getRateText = (option: OutboundRateOption | string | null) =>
  typeof option === "string"
    ? ""
    : option?.rate !== undefined
      ? `$${Number(option.rate).toFixed(2)}`
      : "";

export const getLabelText = (option: OutboundRateOption | string | null) =>
  typeof option === "string"
    ? option
    : option
      ? getCarrierServiceLabel(option.carrier, option.service)
      : "";

export const getDeliveryText = (option: OutboundRateOption | string | null) =>
  typeof option === "string"
    ? ""
    : option?.deliveryDays
      ? `${option.deliveryDays} day${option.deliveryDays === "1" ? "" : "s"}`
      : "";

const getMarginAmount = (
  totalShippingPaid?: number,
  rate?: number,
  lcgInfo?: LcgInfo,
) => {
  if (!isDef(totalShippingPaid) || !isDef(rate)) {
    return undefined;
  }
  const landedCostDollars = lcgInfo?.showLandedCost
    ? lcgInfo.totalLandedCost / 100
    : 0;
  return totalShippingPaid - rate - landedCostDollars;
};

const formatPlainDate = (date: Temporal.PlainDate): string =>
  date.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

const dateToPlainDate = (date: Date): Temporal.PlainDate | null => {
  if (isNaN(date.getTime())) return null;
  try {
    return Temporal.PlainDate.from({
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    });
  } catch {
    return null;
  }
};

const addBusinessDays = (
  date: Temporal.PlainDate,
  days: number,
  includeSaturday: boolean,
): Temporal.PlainDate | null => {
  // Guard against pathological inputs — shipping transit times are always well
  // under 100 days, so a value larger than that is bad data, not a real rate.
  if (days > 100) return null;
  let result = date;
  let remaining = days;
  while (remaining > 0) {
    result = result.add({ days: 1 });
    const dayOfWeek = result.dayOfWeek;
    if (dayOfWeek === 7) continue;
    if (dayOfWeek === 6 && !includeSaturday) continue;
    remaining--;
  }
  return result;
};

const resolveShipmentDeliveryDate = (
  group: FulfillmentGroupData | undefined,
): Temporal.PlainDate | null => {
  const shipment = group?.shipment;
  if (!shipment) return null;

  try {
    if (shipment.tracker?.estDeliveryDate) {
      const date = dateToPlainDate(shipment.tracker.estDeliveryDate);
      if (date) return date;
    }

    const boughtRate = shipment.boughtRate;
    if (boughtRate?.delivery_date) {
      const date = dateToPlainDate(new Date(boughtRate.delivery_date));
      if (date) return date;
    }

    if (boughtRate?.created_at && boughtRate.delivery_days) {
      const createdAt = dateToPlainDate(new Date(boughtRate.created_at));
      if (createdAt) {
        return addBusinessDays(
          createdAt,
          boughtRate.delivery_days,
          !!boughtRate.saturday_delivery,
        );
      }
    }
  } catch {
    return null;
  }

  return null;
};

const getEstimatedDeliveryDateText = (
  deliveryDays: string,
  resolvedDate: Temporal.PlainDate | null,
): string => {
  if (resolvedDate) {
    return formatPlainDate(resolvedDate);
  }
  const days = parseInt(deliveryDays, 10);
  if (isNaN(days)) {
    return deliveryDays;
  }
  try {
    const date = addBusinessDays(Temporal.Now.plainDateISO(), days, false);
    return date ? formatPlainDate(date) : deliveryDays;
  } catch {
    return deliveryDays;
  }
};

const ShipmentRateSelectionDropdownOption = memo(
  function ShipmentRateSelectionDropdownOption({
    option,
    totalShippingPaid,
    cheapestRate,
    minDeliveryDays,
    hideBadges,
    selected,
  }: {
    option: OutboundRateOption | string | null;
    totalShippingPaid?: number;
    cheapestRate?: number;
    minDeliveryDays?: string;
    hideBadges?: boolean;
    selected?: boolean;
  }) {
    const [overflowRef, setOverflowRef] = useState<HTMLDivElement | null>(null);
    const [titleRef, setTitleRef] = useState<HTMLElement | null>(null);

    const team = useRequiredContext(TeamContext);
    const carrierAccountsContext = useContext(CarrierAccountsContext);

    const carrierAccountName =
      typeof option !== "string" && option?.carrier_account_id
        ? carrierAccountsContext?.getCarrierAccountName(
            option.carrier_account_id,
          )
        : undefined;

    if (option === RedoShopifyDeliveryMethodType.PickUp) {
      return (
        <Flex align="center" dir="row" gap="lg" p="xs">
          <LocalPickupLogo color="ghost" height={14} width={14} />
          <Text fontSize="xs">
            {
              RedoShopifyDeliveryMethodTypeToString[
                option as RedoShopifyDeliveryMethodType
              ]
            }
          </Text>
        </Flex>
      );
    }

    if (option === RedoShopifyDeliveryMethodType.Local) {
      return (
        <Flex align="center" dir="row" gap="lg" p="xs">
          <TruckIcon color="ghost" height={14} width={14} />
          <Text fontSize="xs">
            {
              RedoShopifyDeliveryMethodTypeToString[
                option as RedoShopifyDeliveryMethodType
              ]
            }
          </Text>
        </Flex>
      );
    }

    if (option === RedoShopifyDeliveryMethodType.Freight) {
      return (
        <Flex align="center" dir="row" gap="lg" p="xs">
          <TruckIcon color="ghost" height={14} width={14} />
          <Text fontSize="xs">
            {
              RedoShopifyDeliveryMethodTypeToString[
                option as RedoShopifyDeliveryMethodType
              ]
            }
          </Text>
        </Flex>
      );
    }

    if (option === RedoShopifyDeliveryMethodType.Shipping) {
      return (
        <Flex align="center" dir="row" gap="lg" p="xs">
          <TruckIcon color="ghost" height={14} width={14} />
          <Text fontSize="xs">
            {
              RedoShopifyDeliveryMethodTypeToString[
                option as RedoShopifyDeliveryMethodType
              ]
            }
          </Text>
        </Flex>
      );
    }

    const mainText = getDeliveryText(option)
      ? `${getLabelText(option)} - ${getDeliveryText(option)}`
      : getLabelText(option);
    const lcgInfo = getLcgInfo(option);
    const marginAmount = getMarginAmount(
      totalShippingPaid,
      (option as OutboundRateOption)?.rate,
      lcgInfo,
    );

    const isCheapestRate =
      isDef((option as OutboundRateOption)?.rate) &&
      (option as OutboundRateOption)?.rate === cheapestRate;
    const isFastestRate =
      isDef((option as OutboundRateOption)?.deliveryDays) &&
      (option as OutboundRateOption)?.deliveryDays === minDeliveryDays;

    const showMargin = team?.settings?.checkoutOptimization?.enabled;
    const showRateProvider =
      localStorage.getItem("showRateProvider") === "true";

    let rateProvider: string | undefined = (option as OutboundRateOption)
      ?.provider;
    if (rateProvider === RateProvider.INTERNATIONAL_BRIDGE) {
      rateProvider = "IB";
    } else if (rateProvider === RateProvider.REDO_LANDMARK_GLOBAL) {
      rateProvider = "RLG";
    }

    const showLcgBreakdown =
      localStorage.getItem("showLandedCostBreakdown") === "true";

    const hasBadges = Boolean(
      (option as OutboundRateOption)?.redoRate ||
        (option as OutboundRateOption)?.provider === RateProvider.TIKTOK ||
        carrierAccountName ||
        isCheapestRate ||
        isFastestRate ||
        (showRateProvider && !!rateProvider) ||
        lcgInfo.showLandedCost,
    );

    return (
      <OverflowTooltip
        overflowRef={overflowRef}
        tooltipProps={{ title: mainText }}
        wrapperStyle={{ flexGrow: 1, minWidth: 0 }}
      >
        <Flex className={styles.selection} dir="column" gap="xs">
          {option ? (
            <>
              {/* Row 1: radio | logo | carrier + days | price/margin */}
              <Flex
                align="center"
                dir="row"
                gap="md"
                grow={1}
                justify="space-between"
                overflow="hidden"
                ref={setOverflowRef}
              >
                {!hideBadges && (
                  <div
                    className={
                      selected ? styles.radioSelected : styles.radioUnselected
                    }
                  />
                )}
                {typeof option !== "string" && (
                  <Flex align="center" shrink={0}>
                    <ShippingLogo
                      carrier={option.carrier}
                      size={LogoSize.MEDIUM}
                    />
                  </Flex>
                )}
                <Flex dir="column" gap="none" grow={1} overflow="hidden">
                  <OverflowTooltip
                    overflowRef={titleRef}
                    tooltipProps={{ title: getLabelText(option) ?? "" }}
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      overflow="hidden"
                      ref={setTitleRef}
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {getLabelText(option)}
                    </Text>
                  </OverflowTooltip>
                  {hasBadges && (
                    <Flex dir="row" gap="xs" wrap="wrap">
                      {(option as OutboundRateOption).redoRate && (
                        <RedoBadge size="xs" text="Redo" />
                      )}
                      {(option as OutboundRateOption).provider ===
                        RateProvider.TIKTOK && (
                        <RedoBadge
                          segmentLeading={{ type: "icon", Icon: TiktokIcon }}
                          size="xs"
                          text="TikTok"
                        />
                      )}
                      {carrierAccountName && (
                        <RedoBadge size="xs" text={carrierAccountName} />
                      )}
                      {isCheapestRate && (
                        <RedoBadge size="xs" text="Cheapest" />
                      )}
                      {isFastestRate && <RedoBadge size="xs" text="Fastest" />}
                      {showRateProvider && !!rateProvider && (
                        <RedoBadge size="xs" text={rateProvider} />
                      )}
                    </Flex>
                  )}
                </Flex>
                {(getRateText(option) ||
                  (showMargin && isDef(marginAmount))) && (
                  <Flex
                    className={styles.priceColumn}
                    dir="column"
                    gap="xxs"
                    justify="center"
                  >
                    {getRateText(option) && (
                      <Text fontSize="xs" fontWeight="medium" textAlign="right">
                        {getRateText(option)}
                      </Text>
                    )}
                    {showMargin && isDef(marginAmount) && (
                      <Text
                        fontSize="xxs"
                        textAlign="right"
                        textColor={marginAmount < 0 ? "error" : "success"}
                      >
                        {marginAmount >= 0 ? "+" : ""}
                        {getCurrencyFormatter().format(marginAmount)} margin
                      </Text>
                    )}
                  </Flex>
                )}
              </Flex>
              {/* Row 2: delivery days | fees */}
              {!hideBadges &&
                (getDeliveryText(option) || lcgInfo.showLandedCost) && (
                  <Flex
                    align="center"
                    className={styles.deliveryRow}
                    dir="row"
                    justify="space-between"
                  >
                    {getDeliveryText(option) && (
                      <Text fontSize="xxs" textColor="disabled">
                        {getDeliveryText(option)}
                      </Text>
                    )}
                    {lcgInfo.showLandedCost && (
                      <Text fontSize="xxs" textColor="secondary">
                        {"International fees: "}
                        {getCurrencyFormatter().format(
                          lcgInfo.totalLandedCost / 100,
                        )}
                      </Text>
                    )}
                  </Flex>
                )}
              {lcgInfo.showLandedCost && showLcgBreakdown && (
                <Flex dir="row" gap="xs">
                  <Text fontSize="xxs">
                    {"D: "}
                    {getCurrencyFormatter().format(lcgInfo.duties / 100)}
                  </Text>
                  <Text fontSize="xxs">
                    {"T: "}
                    {getCurrencyFormatter().format(lcgInfo.taxes / 100)}
                  </Text>
                  <Text fontSize="xxs">
                    {"F: "}
                    {getCurrencyFormatter().format(lcgInfo.fees / 100)}
                  </Text>
                </Flex>
              )}
            </>
          ) : (
            <div className={styles.placeholder}>No rate selected</div>
          )}
        </Flex>
      </OverflowTooltip>
    );
  },
);

function outboundRateOptionToId(option: OutboundRateOption | string | null) {
  if (!option) {
    return undefined;
  }
  if (typeof option === "string") {
    return option;
  }

  return `${option.carrier}-${option.service}-${!!option.redoRate}-${option.saturdayDelivery}-${option.carrier_account_id}`;
}

// If a label was purchased outside of redo, but the merchant added manual fulfillment info, the only data we have is the carrier. So show that instead of the entire dropdown
export const SimpleShipmentRateSelectionDropdown = memo(
  function SimpleShipmentRateSelectionDropdown({
    groups,
  }: {
    groups: readonly FulfillmentGroupData[];
  }) {
    const allFreight = groups.every(
      (group) => group.shipmentDetails?.freightShipment,
    );

    if (allFreight) {
      const allScacs = groups
        .map((group) => group.shipmentDetails?.freightShipment?.scac)
        .filter((scac): scac is KnownSCAC => !!scac);

      const uniqueScacs = [...new Set(allScacs)];
      const totalCost = groups.reduce((sum, group) => {
        return (
          sum + parseFloat(group.shipmentDetails?.freightShipment?.cost ?? "0")
        );
      }, 0);
      return (
        <Flex dir="column" gap="sm">
          <Text fontSize="xs">Service</Text>
          {uniqueScacs.length === 1 ? (
            <Flex align="center" dir="row" gap="sm">
              <img
                alt={knownScacToLabel[uniqueScacs[0]!]}
                className={styles.freightLogo}
                src={getFreightLogoUrl(uniqueScacs[0]!)}
              />
              <Text fontSize="xs">{knownScacToLabel[uniqueScacs[0]!]}</Text>
              <Text fontSize="xs">
                - {getCurrencyFormatter().format(totalCost)}
              </Text>
            </Flex>
          ) : (
            <Text fontSize="xs">
              {uniqueScacs.length === 0 ? "No carrier" : "Mixed carriers"}
            </Text>
          )}
        </Flex>
      );
    }
    const noCarriers = groups.every((group) => !group.fulfillmentInfo?.carrier);

    const carrier = groups.every(
      (group) =>
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        group.fulfillmentInfo?.carrier === groups[0].fulfillmentInfo?.carrier,
    )
      ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        groups[0].fulfillmentInfo?.carrier
      : null;

    return (
      <Flex dir="column" gap="sm">
        <Text fontSize="xs">Service</Text>
        {carrier ? (
          <Flex align="center" dir="row" gap="sm">
            <ShippingLogo carrier={carrier} size={LogoSize.MEDIUM} />
            <Text fontSize="xs">{carrierToLabel[carrier]}</Text>
          </Flex>
        ) : (
          <Text fontSize="xs">
            {noCarriers ? "No carrier" : "Mixed carriers"}
          </Text>
        )}
      </Flex>
    );
  },
);

export const ShipmentRateSelectionDropdown = memo(
  function ShipmentRateSelectionDropdown({
    disabled,
    label,
    options,
    selectedRate,
    setSelectedRate,
    deliveryMethod,
    loading,
    mixedRates,
    fullWidth,
    totalShippingPaid,
    includeAlternateDeliveryMethods,
    usedShippingLearning,
    fulfillmentGroups,
  }: {
    disabled?: boolean;
    label?: string;
    options: readonly OutboundRateOption[];
    selectedRate: OutboundRateOption | null;
    mixedRates: boolean;
    deliveryMethod?: RedoShopifyDeliveryMethodType | null;
    setSelectedRate:
      | ((rate: OutboundRateOption | RedoShopifyDeliveryMethodType) => void)
      | ((rate: OutboundRateOption) => void);
    loading?: boolean;
    fullWidth: boolean;
    totalShippingPaid?: number | undefined;
    includeAlternateDeliveryMethods?: boolean;
    usedShippingLearning?: boolean | null;
    fulfillmentGroups?: readonly FulfillmentGroupData[];
  }) {
    const team = useRequiredContext(TeamContext);
    const { executeWithPinCheck } = usePinVerification();

    const onRateChange = ({
      item,
    }: {
      item: RedoListItem<
        string | OutboundRateOption | RedoShopifyDeliveryMethodType
      >;
    }) => {
      executeWithPinCheck(
        () => setSelectedRate(item.value as OutboundRateOption),
        { type: PinVerificationType.SelectRate },
        { title: "Select rate PIN verification" },
      );
    };

    const [dropdownButtonRef, setDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const saturdayDeliveryOptions = options.filter(
      (option) => option.saturdayDelivery,
    );
    const nonSaturdayDeliveryOptions = options.filter(
      (option) => !option.saturdayDelivery,
    );

    const cheapestRate = options.reduce((min: number | undefined, option) => {
      if (option.rate === undefined) {
        return min;
      }
      if (min === undefined) {
        return option.rate;
      }
      return Number(option.rate) < Number(min) ? option.rate : min;
    }, options[0]?.rate);

    const minDeliveryDays = options.reduce(
      (min: string | undefined, option) => {
        if (option.deliveryDays === undefined) {
          return min;
        }
        if (min === undefined) {
          return option.deliveryDays;
        }
        return Number(option.deliveryDays) < Number(min)
          ? option.deliveryDays
          : min;
      },
      options[0]?.deliveryDays,
    );

    const displayedOption =
      deliveryMethod !== RedoShopifyDeliveryMethodType.Shipping &&
      deliveryMethod
        ? deliveryMethod
        : selectedRate;

    const dropdownOptions: (
      | OutboundRateOption
      | RedoShopifyDeliveryMethodType
    )[] = [
      ...saturdayDeliveryOptions,
      ...nonSaturdayDeliveryOptions,
      ...(includeAlternateDeliveryMethods
        ? [
            RedoShopifyDeliveryMethodType.PickUp,
            RedoShopifyDeliveryMethodType.Local,
            ...(options.length === 0 &&
            deliveryMethod !== RedoShopifyDeliveryMethodType.Shipping
              ? [RedoShopifyDeliveryMethodType.Shipping]
              : []),
            ...(team.settings.outboundLabels?.freightSettings?.freightEnabled &&
            fulfillmentGroups?.every((g) =>
              g.providers?.every((p) =>
                supportedFreightMarketplaces.includes(p),
              ),
            )
              ? [RedoShopifyDeliveryMethodType.Freight]
              : []),
          ]
        : []),
    ];

    const dropdownOptionItems: RedoListItem<
      string | OutboundRateOption | RedoShopifyDeliveryMethodType
    >[] = [...dropdownOptions].map((option) => ({
      id: outboundRateOptionToId(option)!,
      value: option,
      type: "custom",
      selectionVariant: "none",
      render: (
        <ShipmentRateSelectionDropdownOption
          cheapestRate={cheapestRate}
          minDeliveryDays={minDeliveryDays}
          option={option}
          selected={
            outboundRateOptionToId(option) ===
            outboundRateOptionToId(displayedOption)
          }
          totalShippingPaid={totalShippingPaid}
        />
      ),
      useWrapper: true,
    }));

    const hasSaturdayDelivery = useMemo(() => {
      if (!fulfillmentGroups?.length) return false;
      return fulfillmentGroups.some((group) => group.saturdayDelivery === true);
    }, [fulfillmentGroups]);

    const saturdayDeliveryHeader: RedoListItem<
      string | OutboundRateOption | RedoShopifyDeliveryMethodType
    > = {
      id: "saturday-delivery-header",
      value: "saturday-delivery-header",
      type: "section",
      text: "Saturday delivery",
    };

    const nonSaturdayDeliveryHeader: RedoListItem<
      string | OutboundRateOption | RedoShopifyDeliveryMethodType
    > = {
      id: "non-saturday-delivery-header",
      value: "non-saturday-delivery-header",
      type: "section",
      text: "Non-Saturday delivery",
    };

    const finalDropdownItems = [...dropdownOptionItems];
    if (hasSaturdayDelivery && options.length > 0) {
      const nonSaturdayDeliveryIndex = dropdownOptionItems.findIndex(
        (option) => {
          if (typeof option.value === "string") return false;
          return option.value.saturdayDelivery === false;
        },
      );

      if (nonSaturdayDeliveryIndex > 0) {
        finalDropdownItems.splice(
          nonSaturdayDeliveryIndex,
          0,
          nonSaturdayDeliveryHeader,
        );
      }

      finalDropdownItems.unshift(saturdayDeliveryHeader);
    }

    // Check if all groups are in a done status
    const allDone = fulfillmentGroups?.every((group) =>
      isDoneStatus(group.status),
    );

    // Aggregate cell data from all groups
    const aggregatedCellData = useMemo(() => {
      if (!allDone || !fulfillmentGroups?.length) {
        return null;
      }

      const allCellData = fulfillmentGroups.map(getShippingSelectionCellData);

      // Check if all groups have the same carrier and service
      const firstData = allCellData[0];
      if (!firstData) {
        return null;
      }

      const allSame = allCellData.every(
        (data) =>
          data?.carrierName === firstData.carrierName &&
          data?.serviceName === firstData.serviceName,
      );

      if (!allSame) {
        return null; // Mixed carriers/services
      }

      // Aggregate the rate (sum across all groups)
      const totalRate = allCellData.reduce((sum, data) => {
        if (data?.rate) {
          return sum + parseFloat(data.rate);
        }
        return sum;
      }, 0);

      return {
        carrierName: firstData.carrierName,
        serviceName: firstData.serviceName,
        rate: totalRate > 0 ? totalRate.toFixed(2) : firstData.rate,
        deliveryDays: firstData.deliveryDays,
      };
    }, [allDone, fulfillmentGroups]);

    // Check if all groups have the same redoRate and provider
    const aggregatedBoughtRateInfo = useMemo(() => {
      if (!allDone || !fulfillmentGroups?.length) {
        return null;
      }

      const boughtRates = fulfillmentGroups.map(
        (group) => group.shipment?.boughtRate,
      );
      const firstBoughtRate = boughtRates[0];

      if (!firstBoughtRate) {
        return null;
      }

      const allSameRedoRate = boughtRates.every(
        (rate) =>
          !!rate &&
          rate.is_merchant_carrier_account ===
            firstBoughtRate.is_merchant_carrier_account,
      );

      const allSameProvider = boughtRates.every(
        (rate) => rate?.provider === firstBoughtRate.provider,
      );

      return {
        redoRate: allSameRedoRate
          ? !firstBoughtRate.is_merchant_carrier_account
          : false,
        provider: allSameProvider ? firstBoughtRate.provider : undefined,
      };
    }, [allDone, fulfillmentGroups]);

    const selectedItem:
      | RedoListItem<
          string | OutboundRateOption | RedoShopifyDeliveryMethodType
        >
      | undefined = useMemo(() => {
      // For done status groups, use the aggregated cell data from fulfillmentInfo
      if (allDone && aggregatedCellData) {
        // Construct an OutboundRateOption from the aggregated cell data
        const rateOption: OutboundRateOption = {
          carrier: aggregatedCellData.carrierName!,
          service: aggregatedCellData.serviceName!,
          rate: aggregatedCellData.rate
            ? parseFloat(aggregatedCellData.rate)
            : undefined,
          deliveryDays: aggregatedCellData.deliveryDays?.toString(),
          redoRate: aggregatedBoughtRateInfo?.redoRate ?? false,
          provider: aggregatedBoughtRateInfo?.provider,
          saturdayDelivery: false,
          key: `${aggregatedCellData.carrierName}-${aggregatedCellData.serviceName}`,
        };

        return {
          id: `${aggregatedCellData.carrierName}-${aggregatedCellData.serviceName}`,
          value: rateOption,
          type: "custom",
          useWrapper: true,
          render: (
            <ShipmentRateSelectionDropdownOption
              cheapestRate={cheapestRate}
              hideBadges
              minDeliveryDays={minDeliveryDays}
              option={rateOption}
              totalShippingPaid={totalShippingPaid}
            />
          ),
        };
      }

      if (!displayedOption) {
        return undefined;
      }

      return {
        id: outboundRateOptionToId(displayedOption)!,
        value: displayedOption,
        type: "custom",
        useWrapper: true,
        render: loading ? (
          <SkeletonText classNames={styles.tallSkeletonText} length={80} />
        ) : (
          <ShipmentRateSelectionDropdownOption
            cheapestRate={cheapestRate}
            hideBadges
            minDeliveryDays={minDeliveryDays}
            option={displayedOption}
            totalShippingPaid={totalShippingPaid}
          />
        ),
      };
    }, [
      displayedOption,
      totalShippingPaid,
      loading,
      cheapestRate,
      minDeliveryDays,
      allDone,
      aggregatedCellData,
      aggregatedBoughtRateInfo,
    ]);

    const carrierTrademarkText = useMemo(() => {
      if (!displayedOption) return false;
      return (
        typeof displayedOption === "object" &&
        "carrier" in displayedOption &&
        carrierToTrademarkText[displayedOption.carrier]
      );
    }, [displayedOption]);

    const resolvedDeliveryDate = useMemo(
      () =>
        fulfillmentGroups?.length === 1
          ? resolveShipmentDeliveryDate(fulfillmentGroups[0])
          : null,
      [fulfillmentGroups],
    );

    return (
      <>
        <Flex dir="column" gap="sm" grow={fullWidth ? 1 : undefined}>
          {label && (
            <Flex gap="xs">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                {label}
              </Text>
              {usedShippingLearning && <LearningIcon />}
              {selectedRate &&
                deliveryMethod === RedoShopifyDeliveryMethodType.Shipping && (
                  <AutomationInfoIcon
                    selectedShippingRateSetBy={
                      fulfillmentGroups?.[0]?.selectedShippingRateSetBy
                    }
                    selectedShippingRateSetByInfo={
                      fulfillmentGroups?.[0]?.selectedShippingRateSetByInfo
                    }
                  />
                )}
            </Flex>
          )}
          <RedoSingleSelectDropdownInputButton
            className={fullWidth ? styles.fullWidth : undefined}
            disabled={disabled}
            isDropdownOpen={dropdownOpen}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            placeholder={mixedRates ? "{mixed}" : "Select shipment rate"}
            ref={setDropdownButtonRef}
            selectedItem={selectedItem}
            size="xs"
          />
          {fulfillmentGroups?.length === 1 &&
            typeof displayedOption === "object" &&
            displayedOption !== null &&
            "deliveryDays" in displayedOption &&
            displayedOption.deliveryDays && (
              <Text fontSize="xxs" textColor="tertiary">
                {`Estimated delivery: ${getEstimatedDeliveryDateText(displayedOption.deliveryDays, resolvedDeliveryDate)} (${getDeliveryText(displayedOption)})`}
              </Text>
            )}
          {carrierTrademarkText && (
            <Text fontSize="xxs" textColor="tertiary">
              {carrierTrademarkText}
            </Text>
          )}
        </Flex>
        <RedoListDropdown
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          fitToAnchor
          items={finalDropdownItems}
          itemSelected={onRateChange}
          itemsLoading={loading}
          selectedItems={selectedItem?.id}
          setDropdownOpen={setDropdownOpen}
        />
      </>
    );
  },
);
